import { useState, useEffect, useCallback, useRef, type ComponentType } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router'
import { useAuthStore, getAuthStore } from '@carouselgg/auth/store'
import { getAccessToken } from '@carouselgg/auth/client'
import { Container } from '@carouselgg/nexus/container'
import { Card } from '@carouselgg/nexus/card'
import { Text } from '@carouselgg/nexus/text'
import { GradientText } from '@carouselgg/nexus/gradient-text'
import { Button } from '@carouselgg/nexus/button'
import { Input } from '@carouselgg/nexus/input'
import { FormField } from '@carouselgg/nexus/form-field'
import { Badge } from '@carouselgg/nexus/badge'
import { Callout } from '@carouselgg/nexus/callout'
import { Stepper } from '@carouselgg/nexus/stepper'
import { ColorPicker } from '@carouselgg/nexus/color-picker'
import { Switch } from '@carouselgg/nexus/switch'
import { Accordion } from '@carouselgg/nexus/accordion'
import { Spinner } from '@carouselgg/nexus/spinner'
import { toast } from '@carouselgg/nexus/toast'
import { DISCORD_INVITE } from '../constants/links'
import {
  Building2, TrophyCup, GraduationCap,
  ChevronRight, ChevronLeft, Check,
  DiscordLogo,
} from '@carouselgg/nexus/icon'
import { createOrganization, checkSlugAvailable, checkNameAvailable, getMyOrganizations, getDiscordGuilds } from '../api/organizations'
import type { MyOrgItem, CreateOrgRequest, DiscordGuild } from '../api/organizations'
import { generateSlug } from '../utils/slug'
import { generateCode } from '../utils/code'
import styles from './CreateOrgPage.module.css'

const API_BASE = import.meta.env.VITE_API_URL || '/api'
const CREATE_ORG_DRAFT_KEY = 'carousel_create_org_draft'

// Color helpers for live preview (same logic as org/src/hooks/useOrgTheme.ts)
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return { r: 0, g: 0, b: 0 }
  return { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
}

function luminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

function textOnBg(bgHex: string): string {
  const { r, g, b } = hexToRgb(bgHex)
  return luminance(r, g, b) > 0.179 ? '#18181b' : '#ffffff'
}

type OrgType = 'organization' | 'league' | 'university'

interface SelectedGuild {
  id: string
  name: string
  icon_url: string
}

interface FormData {
  type: OrgType | null
  name: string
  slug: string
  description: string
  primaryColor: string
  secondaryColor: string
  accentColor: string
  socialLinks: Record<string, string>
  universityName: string
  conference: string
  recruitingOpen: boolean
  registrationOpen: boolean
  discordGuild: SelectedGuild | null
  shortCode: string
}

const INITIAL_FORM: FormData = {
  type: null,
  name: '',
  slug: '',
  description: '',
  primaryColor: '#7c3aed',
  secondaryColor: '#0ea5e9',
  accentColor: '#ec4899',
  socialLinks: {},
  universityName: '',
  conference: '',
  recruitingOpen: false,
  registrationOpen: false,
  discordGuild: null,
  shortCode: '',
}

const TYPE_CARDS: { type: OrgType; icon: ComponentType<{ size?: number | string }>; title: string; description: string; accent: string }[] = [
  {
    type: 'organization',
    icon: Building2,
    title: 'Build an Org',
    description: 'Create your gaming community, build rosters, and compete in leagues. For gaming clubs, esports orgs, and community groups.',
    accent: 'var(--nexus-blue)',
  },
  {
    type: 'league',
    icon: TrophyCup,
    title: 'Run a League',
    description: 'Create and manage competitive seasons with scheduling, standings, and match management.',
    accent: 'var(--nexus-purple)',
  },
  {
    type: 'university',
    icon: GraduationCap,
    title: 'Go Collegiate',
    description: 'Register your university esports program. Manage eligibility, scholarships, and recruit players.',
    accent: 'var(--nexus-green)',
  },
]

const TYPE_LABELS: Record<OrgType, string> = {
  organization: 'Organization',
  league: 'League',
  university: 'University',
}

function getSteps(orgType: OrgType | null): { label: string }[] {
  const steps = [{ label: 'Basics' }]
  if (orgType === 'league' || orgType === 'university') {
    steps.push({ label: 'Details' })
  }
  steps.push({ label: 'Brand' }, { label: 'Socials' }, { label: 'Review' })
  return steps
}

// Maps the internal step index to the step category, accounting for club orgs skipping "Details"
function getStepKey(step: number, orgType: OrgType | null): string {
  const hasDetails = orgType === 'league' || orgType === 'university'
  const keys = hasDetails
    ? ['basics', 'details', 'brand', 'socials', 'review']
    : ['basics', 'brand', 'socials', 'review']
  return keys[step] ?? 'basics'
}

export function CreateOrgPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  const initialType = searchParams.get('type') as OrgType | null
  const hasValidType = initialType && ['organization', 'league', 'university'].includes(initialType)

  // Restore draft from sessionStorage (saved before Discord OAuth redirect)
  const [draft] = useState(() => {
    try {
      const raw = sessionStorage.getItem(CREATE_ORG_DRAFT_KEY)
      if (raw) {
        sessionStorage.removeItem(CREATE_ORG_DRAFT_KEY)
        return JSON.parse(raw) as { form: FormData; step: number; slugManuallyEdited: boolean; codeManuallyEdited?: boolean }
      }
    } catch { /* ignore */ }
    return null
  })

  const [showTypeSelect, setShowTypeSelect] = useState(draft ? false : !hasValidType)
  const [step, setStep] = useState(draft?.step ?? 0)
  const [form, setForm] = useState<FormData>(
    draft?.form ?? { ...INITIAL_FORM, type: hasValidType ? initialType : null },
  )
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(draft?.slugManuallyEdited ?? false)
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(draft?.codeManuallyEdited ?? false)
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(draft ? true : null)
  const [slugChecking, setSlugChecking] = useState(false)
  const [slugReason, setSlugReason] = useState<string | null>(null)
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(draft ? true : null)
  const [nameChecking, setNameChecking] = useState(false)
  const [nameReason, setNameReason] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [capData, setCapData] = useState<{ count: number; max: number; orgs: MyOrgItem[] } | null>(null)
  const [capLoading, setCapLoading] = useState(true)
  const slugTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const nameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Discord guild linking state
  const linkedAccounts = useAuthStore((s) => s.linkedAccounts)
  const hasDiscordLinked = linkedAccounts.some((a) => a.provider === 'discord')
  const [discordGuilds, setDiscordGuilds] = useState<DiscordGuild[]>([])
  const [guildsLoading, setGuildsLoading] = useState(false)
  const [guildsFetched, setGuildsFetched] = useState(false)
  const [guildsError, setGuildsError] = useState<string | null>(null)
  const [needsRelink, setNeedsRelink] = useState(false)

  const steps = getSteps(form.type)
  const stepKey = getStepKey(step, form.type)

  // Auto-fetch Discord guilds when entering the Socials step
  useEffect(() => {
    if (stepKey !== 'socials' || !hasDiscordLinked || guildsFetched) return
    setGuildsFetched(true)
    setGuildsLoading(true)
    setGuildsError(null)
    setNeedsRelink(false)
    getDiscordGuilds()
      .then((guilds) => setDiscordGuilds(guilds))
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'Failed to fetch Discord servers'
        if (msg.includes('expired') || msg.includes('re-link')) {
          setNeedsRelink(true)
        }
        setGuildsError(msg)
      })
      .finally(() => setGuildsLoading(false))
  }, [stepKey, hasDiscordLinked, guildsFetched])

  // Check org cap on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setCapLoading(false)
      return
    }
    getMyOrganizations()
      .then((data) => setCapData({ count: data.count, max: data.max, orgs: data.organizations }))
      .catch(() => setCapData(null))
      .finally(() => setCapLoading(false))
  }, [isAuthenticated])

  // Debounced slug availability check
  const checkSlug = useCallback((slug: string) => {
    if (slugTimerRef.current) clearTimeout(slugTimerRef.current)
    if (slug.length < 2) {
      setSlugAvailable(null)
      setSlugReason(null)
      setSlugChecking(false)
      return
    }
    setSlugChecking(true)
    slugTimerRef.current = setTimeout(() => {
      checkSlugAvailable(slug)
        .then((res) => {
          setSlugAvailable(res.available)
          setSlugReason(res.reason ?? null)
        })
        .catch(() => {
          setSlugAvailable(null)
          setSlugReason(null)
        })
        .finally(() => setSlugChecking(false))
    }, 500)
  }, [])

  // Debounced name availability check
  const checkOrgName = useCallback((name: string) => {
    if (nameTimerRef.current) clearTimeout(nameTimerRef.current)
    if (name.trim().length < 2) {
      setNameAvailable(null)
      setNameReason(null)
      setNameChecking(false)
      return
    }
    setNameChecking(true)
    nameTimerRef.current = setTimeout(() => {
      checkNameAvailable(name.trim())
        .then((res) => {
          setNameAvailable(res.available)
          setNameReason(res.reason ?? null)
        })
        .catch(() => {
          setNameAvailable(null)
          setNameReason(null)
        })
        .finally(() => setNameChecking(false))
    }, 500)
  }, [])

  const updateForm = useCallback((updates: Partial<FormData>) => {
    setForm((prev) => ({ ...prev, ...updates }))
  }, [])

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value
      const updates: Partial<FormData> = { name }
      checkOrgName(name)
      if (!slugManuallyEdited) {
        updates.slug = generateSlug(name)
        checkSlug(updates.slug)
      }
      if (!codeManuallyEdited) {
        updates.shortCode = generateCode(name)
      }
      updateForm(updates)
    },
    [slugManuallyEdited, codeManuallyEdited, updateForm, checkSlug, checkOrgName],
  )

  const handleSlugChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')
      setSlugManuallyEdited(true)
      updateForm({ slug })
      checkSlug(slug)
    },
    [updateForm, checkSlug],
  )

  const handleSocialChange = useCallback(
    (key: string, value: string) => {
      updateForm({
        socialLinks: { ...form.socialLinks, [key]: value },
      })
    },
    [form.socialLinks, updateForm],
  )

  const handleRetryFetchGuilds = useCallback(() => {
    setGuildsFetched(false)
    setGuildsError(null)
    setNeedsRelink(false)
  }, [])

  const handleSelectGuild = useCallback(
    (guild: DiscordGuild) => {
      updateForm({
        discordGuild: { id: guild.id, name: guild.name, icon_url: guild.icon_url },
      })
    },
    [updateForm],
  )

  const handleRemoveGuild = useCallback(() => {
    updateForm({ discordGuild: null })
  }, [updateForm])

  const handleConnectDiscord = useCallback(async () => {
    // Save form draft so we can restore after OAuth redirect
    sessionStorage.setItem(CREATE_ORG_DRAFT_KEY, JSON.stringify({ form, step, slugManuallyEdited, codeManuallyEdited }))
    const refresh = getAuthStore().getState().refresh
    await refresh()
    const token = getAccessToken()
    if (!token) return
    const returnTo = `${window.location.origin}/create-org?type=${form.type ?? ''}`
    window.location.href = `${API_BASE}/auth/oauth/discord?token=${encodeURIComponent(token)}&return_to=${encodeURIComponent(returnTo)}`
  }, [form, step, slugManuallyEdited, codeManuallyEdited])

  const handleTypeSelect = useCallback(
    (type: OrgType) => {
      updateForm({ type })
      setShowTypeSelect(false)
      setStep(0)
    },
    [updateForm],
  )

  const handleSubmit = useCallback(async () => {
    if (!form.type) return
    setIsSubmitting(true)
    try {
      const req: CreateOrgRequest = {
        name: form.name,
        slug: form.slug,
        description: form.description,
        type: form.type,
        primary_color: form.primaryColor,
        secondary_color: form.secondaryColor,
        accent_color: form.accentColor,
        social_links: form.socialLinks,
      }
      if (form.type === 'university') {
        req.university_name = form.universityName
        req.conference = form.conference
        req.recruiting_open = form.recruitingOpen
      }
      if (form.type === 'league') {
        req.registration_open = form.registrationOpen
      }
      if (form.discordGuild) {
        req.discord_guild_id = form.discordGuild.id
        req.discord_guild_name = form.discordGuild.name
        req.discord_guild_icon = form.discordGuild.icon_url
      }
      if (form.shortCode) {
        req.short_code = form.shortCode
      }
      const result = await createOrganization(req)
      if (result.organization.status === 'pending_verification') {
        toast.success('University submitted for verification!')
        navigate(`/org/${result.organization.slug}`)
      } else {
        toast.success('Organization created!')
        navigate(`/org/${result.organization.slug}`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsSubmitting(false)
    }
  }, [form, navigate])

  const canAdvance = (): boolean => {
    switch (stepKey) {
      case 'basics':
        return form.name.trim().length >= 2 && form.slug.length >= 2 && slugAvailable === true && nameAvailable === true
      case 'details':
        if (form.type === 'university') return form.universityName.trim().length > 0
        return true
      case 'brand':
        return true
      case 'socials':
        if (form.type === 'university') return !!form.discordGuild
        return true
      default:
        return true
    }
  }

  // Loading state
  if (capLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
      </div>
    )
  }

  // Org cap reached
  if (capData && capData.count >= capData.max) {
    return (
      <Container size="sm">
        <div className={styles.capReached}>
          <Text as="h1" size="xl" weight="bold">Organization Limit Reached</Text>
          <Text muted>
            You can belong to a maximum of {capData.max} organizations. To create a new one, you'll need to leave an existing organization.
          </Text>
          <div className={styles.capOrgList}>
            {capData.orgs.map((org) => (
              <Link key={org.id} to={`/org/${org.slug}`} className={styles.capOrgLink}>
                <Card interactive>
                  <div className={styles.capOrgCard}>
                    <div className={styles.capOrgAvatar} style={{ background: org.primary_color }}>
                      <Text as="span" size="sm" weight="bold" style={{ color: '#fff' }}>
                        {org.name.charAt(0)}
                      </Text>
                    </div>
                    <div>
                      <Text weight="semibold">{org.name}</Text>
                      <div className={styles.capOrgMeta}>
                        <Badge size="sm">{TYPE_LABELS[org.type as OrgType] ?? org.type}</Badge>
                        <Text size="xs" muted>{org.role}</Text>
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </Container>
    )
  }

  // Type selection screen
  if (showTypeSelect) {
    return (
      <Container size="md">
        <div className={styles.typeSelect}>
          <div className={styles.pageHeader}>
            <h1><GradientText className={styles.pageTitle}>Create an Organization</GradientText></h1>
            <Text size="lg" muted>What are you building?</Text>
          </div>
          <div className={styles.typeCards}>
            {TYPE_CARDS.map(({ type, icon: Icon, title, description, accent }) => (
              <button
                key={type}
                type="button"
                className={styles.typeCard}
                onClick={() => handleTypeSelect(type)}
              >
                <Card interactive accent={accent} padding="xl">
                  <div className={styles.typeCardInner}>
                    <div className={styles.typeIconWrap} style={{ color: accent }}>
                      <Icon size={32} />
                    </div>
                    <Text as="h3" size="lg" weight="semibold">{title}</Text>
                    <Text size="sm" muted>{description}</Text>
                    <span className={styles.typeArrow} style={{ color: accent }}>
                      <ChevronRight size={18} />
                    </span>
                  </div>
                </Card>
              </button>
            ))}
          </div>
        </div>
      </Container>
    )
  }

  return (
    <Container size="sm">
      <div className={styles.formPage}>
        {/* Header */}
        <div className={styles.pageHeader}>
          <h1>
            <GradientText className={styles.pageTitle}>
              Create {form.type === 'league' ? 'a League' : form.type === 'university' ? 'a University Program' : 'an Organization'}
            </GradientText>
          </h1>
          <Badge size="sm">{TYPE_LABELS[form.type!]}</Badge>
        </div>

        {/* Stepper */}
        <Stepper steps={steps} activeStep={step} />

        {/* Step content */}
        <div className={styles.stepContent}>

          {/* ── Step: Basics ── */}
          {stepKey === 'basics' && (
            <div className={styles.stepFields}>
              <div className={styles.nameCodeRow}>
                <FormField
                  label="Organization Name"
                  error={
                    form.name.length > 0 && form.name.trim().length < 2
                      ? 'Name must be at least 2 characters'
                      : nameAvailable === false
                        ? nameReason ?? 'This name is already taken'
                        : undefined
                  }
                >
                  <Input
                    value={form.name}
                    onChange={handleNameChange}
                    placeholder="e.g. Neon Knights Gaming"
                    maxLength={100}
                    autoFocus
                    trailIcon={
                      nameChecking
                        ? <Spinner size="sm" />
                        : nameAvailable === true
                          ? <Check size={16} style={{ color: 'var(--nexus-success)' }} />
                          : undefined
                    }
                  />
                </FormField>

                <FormField label="Code">
                  <Input
                    value={form.shortCode}
                    onChange={(e) => {
                      setCodeManuallyEdited(true)
                      updateForm({ shortCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4) })
                    }}
                    placeholder="e.g. TSM"
                    maxLength={4}
                  />
                </FormField>
              </div>

              <FormField
                label="URL Slug"
                error={
                  form.slug.length > 0 && form.slug.length < 2
                    ? 'Slug must be at least 2 characters'
                    : slugAvailable === false
                      ? slugReason ?? 'This slug is already taken'
                      : undefined
                }
              >
                <Input
                  value={form.slug}
                  onChange={handleSlugChange}
                  placeholder="neon-knights-gaming"
                  maxLength={60}
                  trailIcon={
                    slugChecking
                      ? <Spinner size="sm" />
                      : slugAvailable === true
                        ? <Check size={16} style={{ color: 'var(--nexus-success)' }} />
                        : undefined
                  }
                />
                <Text size="xs" muted className={styles.slugPreview}>
                  www.carousel.gg/org/{form.slug || 'your-slug'}
                </Text>
              </FormField>

              <FormField label="Description">
                <textarea
                  value={form.description}
                  onChange={(e) => updateForm({ description: e.target.value })}
                  placeholder="Tell people what your organization is about..."
                  maxLength={500}
                  rows={3}
                  className={styles.textarea}
                />
                <Text size="xs" muted className={styles.charCount}>
                  {form.description.length}/500
                </Text>
              </FormField>

            </div>
          )}

          {/* ── Step: Type Details (League) ── */}
          {stepKey === 'details' && form.type === 'league' && (
            <div className={styles.stepFields}>
              <Callout variant="info">
                As a league, you'll be able to create seasons, set up divisions, manage standings, configure rules, and run matches. You can enable registration now or wait until you've set up your first season.
              </Callout>
              <div className={styles.switchRow}>
                <div>
                  <Text weight="semibold">Open Registration</Text>
                  <Text size="sm" muted>Allow teams and players to register for your league</Text>
                </div>
                <Switch
                  checked={form.registrationOpen}
                  onChange={(e) => updateForm({ registrationOpen: e.target.checked })}
                />
              </div>
            </div>
          )}

          {/* ── Step: Type Details (University) ── */}
          {stepKey === 'details' && form.type === 'university' && (
            <div className={styles.stepFields}>
              <Callout variant="info">
                University organizations connect to your academic institution. You'll be able to manage esports programs, scholarships, eligibility requirements, and recruit players.
              </Callout>
              <FormField
                label="University Name"
                error={form.universityName.length === 0 ? undefined : form.universityName.trim().length < 2 ? 'University name is required' : undefined}
              >
                <Input
                  value={form.universityName}
                  onChange={(e) => updateForm({ universityName: e.target.value })}
                  placeholder="e.g. University of California, Berkeley"
                  autoFocus
                />
              </FormField>
              <FormField label="Conference (optional)">
                <Input
                  value={form.conference}
                  onChange={(e) => updateForm({ conference: e.target.value })}
                  placeholder="e.g. Big Ten, PAC-12"
                />
              </FormField>
              <div className={styles.switchRow}>
                <div>
                  <Text weight="semibold">Open Recruiting</Text>
                  <Text size="sm" muted>Allow players to express interest in joining your program</Text>
                </div>
                <Switch
                  checked={form.recruitingOpen}
                  onChange={(e) => updateForm({ recruitingOpen: e.target.checked })}
                />
              </div>
            </div>
          )}

          {/* ── Step: Brand ── */}
          {stepKey === 'brand' && (() => {
            const { r, g, b } = hexToRgb(form.primaryColor)
            const primaryRgb = `${r}, ${g}, ${b}`
            const textColor = textOnBg(form.primaryColor)
            const initial = form.name.trim().charAt(0).toUpperCase() || 'A'

            return (
              <div className={styles.stepFields}>
                <div>
                  <Text as="h2" size="lg" weight="semibold">Brand Your Organization</Text>
                  <Text size="sm" muted>Choose colors that represent your community. These will be used across your org's public page.</Text>
                </div>

                {/* Live preview */}
                <div className={styles.brandPreview}>
                  <div
                    className={styles.brandPreviewHero}
                    style={{
                      background: `radial-gradient(ellipse 80% 60% at 50% 40%, rgba(${primaryRgb}, 0.35), transparent 70%), radial-gradient(ellipse 60% 80% at 20% 80%, rgba(${primaryRgb}, 0.18), transparent 60%), linear-gradient(180deg, #0a0a0a 0%, #111111 100%)`,
                    }}
                  >
                    <div
                      className={styles.brandPreviewLogo}
                      style={{ backgroundColor: form.primaryColor, color: textColor }}
                    >
                      {initial}
                    </div>
                    <span className={styles.brandPreviewName}>
                      {form.name.trim() || 'Your Organization'}
                    </span>
                  </div>
                  <div
                    className={styles.brandPreviewBar}
                    style={{ background: `linear-gradient(90deg, ${form.primaryColor}, ${form.secondaryColor})` }}
                  />
                </div>

                <ColorPicker
                  value={form.primaryColor}
                  onChange={(color) => updateForm({ primaryColor: color })}
                  label="Primary Color"
                />

                <Accordion label="Advanced Colors" compact>
                  <div className={styles.advancedColors}>
                    <ColorPicker
                      value={form.secondaryColor}
                      onChange={(color) => updateForm({ secondaryColor: color })}
                      label="Secondary Color"
                    />
                    <ColorPicker
                      value={form.accentColor}
                      onChange={(color) => updateForm({ accentColor: color })}
                      label="Accent Color"
                    />
                  </div>
                </Accordion>

                <Callout variant="info">
                  You can upload your logo after creating your organization.
                </Callout>
              </div>
            )
          })()}

          {/* ── Step: Social Links ── */}
          {stepKey === 'socials' && (
            <div className={styles.stepFields}>
              {form.type === 'university' ? (
                <Callout variant="info">
                  University organizations require a linked Discord account and server for verification. A member of the Carousel team will reach out to the admin on Discord to verify your university. Need to expedite? Contact us at{' '}
                  <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--nexus-primary)' }}>discord.gg/carousel</a>
                </Callout>
              ) : (
                <Text size="sm" muted>All fields are optional. You can always add these later.</Text>
              )}

              {/* Discord Server Linking */}
              <div className={styles.discordSection}>
                <Text as="h3" size="base" weight="semibold">
                  <span className={styles.discordSectionTitle}>
                    <DiscordLogo size={20} />
                    Discord Server
                  </span>
                </Text>

                {!hasDiscordLinked ? (
                  <div>
                    <Text size="sm" muted style={{ marginBottom: 'var(--nexus-space-sm)' }}>
                      Connect your Discord account to link your server.
                    </Text>
                    <Button variant="secondary" size="sm" onClick={handleConnectDiscord}>
                      <DiscordLogo size={16} />
                      Connect Discord
                    </Button>
                  </div>
                ) : form.discordGuild ? (
                  <div className={styles.selectedGuild}>
                    <div className={styles.guildInfo}>
                      {form.discordGuild.icon_url ? (
                        <img
                          src={form.discordGuild.icon_url}
                          alt=""
                          className={styles.guildIcon}
                        />
                      ) : (
                        <div className={styles.guildIconPlaceholder}>
                          {form.discordGuild.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <Text weight="semibold">{form.discordGuild.name}</Text>
                        <Text size="xs" muted>Verified server</Text>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleRemoveGuild}>
                      Remove
                    </Button>
                  </div>
                ) : guildsLoading ? (
                  <div className={styles.guildLoadingRow}>
                    <Spinner size="sm" />
                    <Text size="sm" muted>Loading your Discord servers...</Text>
                  </div>
                ) : needsRelink ? (
                  <div>
                    <Text size="sm" muted style={{ marginBottom: 'var(--nexus-space-sm)' }}>
                      Your Discord authorization has expired.
                    </Text>
                    <Button variant="secondary" size="sm" onClick={handleConnectDiscord}>
                      <DiscordLogo size={16} />
                      Re-connect Discord
                    </Button>
                  </div>
                ) : guildsError ? (
                  <div>
                    <Text size="xs" style={{ color: 'var(--nexus-error)', marginBottom: 'var(--nexus-space-sm)' }}>
                      {guildsError}
                    </Text>
                    <Button variant="ghost" size="sm" onClick={handleRetryFetchGuilds}>
                      Retry
                    </Button>
                  </div>
                ) : discordGuilds.length === 0 ? (
                  <Text size="sm" muted>You don't manage any Discord servers.</Text>
                ) : (
                  <div className={styles.guildPicker}>
                    {discordGuilds.map((guild) => (
                      <button
                        key={guild.id}
                        type="button"
                        className={styles.guildOption}
                        disabled={guild.already_linked}
                        onClick={() => handleSelectGuild(guild)}
                      >
                        {guild.icon_url ? (
                          <img src={guild.icon_url} alt="" className={styles.guildIcon} />
                        ) : (
                          <div className={styles.guildIconPlaceholder}>
                            {guild.name.charAt(0)}
                          </div>
                        )}
                        <div className={styles.guildOptionText}>
                          <Text size="sm" weight="semibold">{guild.name}</Text>
                          {guild.already_linked && (
                            <Text size="xs" muted>Already linked to another org</Text>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {form.discordGuild && (
                <FormField label="Discord Invite Link (optional)">
                  <Input
                    value={form.socialLinks.discord ?? ''}
                    onChange={(e) => handleSocialChange('discord', e.target.value)}
                    placeholder="https://discord.gg/your-server"
                  />
                  <Text size="xs" muted>Add an invite link so players can join your server from your org page.</Text>
                </FormField>
              )}
              <FormField label="Website">
                <Input
                  value={form.socialLinks.website ?? ''}
                  onChange={(e) => handleSocialChange('website', e.target.value)}
                  placeholder="https://your-org.gg"
                />
              </FormField>
              <FormField label="Twitch">
                <Input
                  value={form.socialLinks.twitch ?? ''}
                  onChange={(e) => handleSocialChange('twitch', e.target.value)}
                  placeholder="https://twitch.tv/your-channel"
                />
              </FormField>
              <FormField label="YouTube">
                <Input
                  value={form.socialLinks.youtube ?? ''}
                  onChange={(e) => handleSocialChange('youtube', e.target.value)}
                  placeholder="https://youtube.com/@your-channel"
                />
              </FormField>
              <FormField label="Instagram">
                <Input
                  value={form.socialLinks.instagram ?? ''}
                  onChange={(e) => handleSocialChange('instagram', e.target.value)}
                  placeholder="https://instagram.com/your-handle"
                />
              </FormField>
            </div>
          )}

          {/* ── Step: Review ── */}
          {stepKey === 'review' && (
            <div className={styles.stepFields}>
              <Card>
                <div className={styles.reviewContent}>
                  <div className={styles.reviewHeader}>
                    {form.discordGuild?.icon_url ? (
                      <img src={form.discordGuild.icon_url} alt="" className={styles.reviewAvatarImg} />
                    ) : (
                      <div className={styles.reviewAvatar} style={{ background: form.primaryColor }}>
                        <Text as="span" size="xl" weight="bold" style={{ color: '#fff' }}>
                          {form.name.charAt(0).toUpperCase()}
                        </Text>
                      </div>
                    )}
                    <div>
                      <Text as="h3" size="lg" weight="bold">{form.name}</Text>
                      <div className={styles.reviewMeta}>
                        <Badge size="sm">{TYPE_LABELS[form.type!]}</Badge>
                        <Text size="xs" muted>www.carousel.gg/org/{form.slug}</Text>
                      </div>
                    </div>
                  </div>

                  {form.description && (
                    <div className={styles.reviewSection}>
                      <Text size="sm" muted>Description</Text>
                      <Text size="sm">{form.description}</Text>
                    </div>
                  )}

                  {form.shortCode && (
                    <div className={styles.reviewSection}>
                      <Text size="sm" muted>Code</Text>
                      <Text size="sm" weight="bold">{form.shortCode}</Text>
                    </div>
                  )}

                  <div className={styles.reviewSection}>
                    <Text size="sm" muted>Brand Colors</Text>
                    <div className={styles.reviewColors}>
                      <div className={styles.reviewSwatch} style={{ background: form.primaryColor }} title="Primary" />
                      <div className={styles.reviewSwatch} style={{ background: form.secondaryColor }} title="Secondary" />
                      <div className={styles.reviewSwatch} style={{ background: form.accentColor }} title="Accent" />
                    </div>
                  </div>

                  {form.type === 'university' && form.universityName && (
                    <div className={styles.reviewSection}>
                      <Text size="sm" muted>University</Text>
                      <Text size="sm">{form.universityName}{form.conference ? ` — ${form.conference}` : ''}</Text>
                    </div>
                  )}

                  {form.type === 'league' && (
                    <div className={styles.reviewSection}>
                      <Text size="sm" muted>Registration</Text>
                      <Badge size="sm" variant={form.registrationOpen ? 'success' : 'default'}>
                        {form.registrationOpen ? 'Open' : 'Closed'}
                      </Badge>
                    </div>
                  )}

                  {form.type === 'university' && (
                    <div className={styles.reviewSection}>
                      <Text size="sm" muted>Recruiting</Text>
                      <Badge size="sm" variant={form.recruitingOpen ? 'success' : 'default'}>
                        {form.recruitingOpen ? 'Open' : 'Closed'}
                      </Badge>
                    </div>
                  )}

                  {form.discordGuild && (
                    <div className={styles.reviewSection}>
                      <Text size="sm" muted>Discord Server</Text>
                      <div className={styles.guildInfo}>
                        {form.discordGuild.icon_url ? (
                          <img src={form.discordGuild.icon_url} alt="" className={styles.guildIcon} />
                        ) : (
                          <div className={styles.guildIconPlaceholder}>
                            {form.discordGuild.name.charAt(0)}
                          </div>
                        )}
                        <Text size="sm" weight="semibold">{form.discordGuild.name}</Text>
                      </div>
                    </div>
                  )}

                  {Object.values(form.socialLinks).some(Boolean) && (
                    <div className={styles.reviewSection}>
                      <Text size="sm" muted>Social Links</Text>
                      <div className={styles.reviewSocials}>
                        {Object.entries(form.socialLinks)
                          .filter(([, v]) => v)
                          .map(([key, url]) => (
                            <Text key={key} size="xs">
                              <strong>{key}:</strong> {url}
                            </Text>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>

        {/* Navigation buttons */}
        <div className={styles.navButtons}>
          <Button
            variant="ghost"
            onClick={() => {
              if (step === 0) {
                if (hasValidType) {
                  navigate('/')
                } else {
                  setShowTypeSelect(true)
                }
              } else {
                setStep(step - 1)
              }
            }}
          >
            <ChevronLeft size={16} />
            Back
          </Button>

          {stepKey === 'review' ? (
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isSubmitting}
            >
              <Check size={16} />
              Create Organization
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
            >
              {stepKey === 'socials' ? 'Review' : 'Next'}
              <ChevronRight size={16} />
            </Button>
          )}
        </div>
      </div>
    </Container>
  )
}
